import React, { Component } from "react";
import emailjs from 'emailjs-com';
import './Message.css';
import { addMessage, getMessages } from './store';
import { isSupabaseConfigured } from './supabaseClient';

// Notification par mail à chaque message reçu.
// Mettre à true et renseigner les identifiants EmailJS pour l'activer.
const EMAIL_NOTIFICATIONS_ENABLED = false;

if (EMAIL_NOTIFICATIONS_ENABLED) {
  emailjs.init("YOUR_USER_ID");
}

// Le flou du filtre goo (stdDeviation 9) arrondit les angles de la forme.
// Sans compensation, la forme bleue paraît plus arrondie que le bouton et
// laisse dépasser ses coins — d'autant plus visible que le bouton est petit.
const ARRONDI_DU_FLOU = 9;

// Cale la forme goo exactement sur le bouton (position, taille, arrondi)
// et renvoie le rectangle du bouton.
const caleSurBouton = (rect, btn) => {
  const r = btn.getBoundingClientRect();
  rect.style.transform = `translate(${r.left}px, ${r.top}px)`;
  rect.style.width = `${r.width}px`;
  rect.style.height = `${r.height}px`;
  const radiusBouton = parseFloat(getComputedStyle(btn).borderTopLeftRadius) || 0;
  rect.style.borderRadius = `${Math.max(0, radiusBouton - ARRONDI_DU_FLOU)}px`;
  return r;
};

class Message extends Component {
  constructor(props) {
    super();
    this.state = {
      speech: [
        { content: "Hello !", type: "admin" },
        { type: "admin", img: "/hello.gif", alt: "Hello gif" },
        { content: "Hello !", type: "client", autor: "Milo" },
        { content: "I'm Patrick", type: "client", autor: "Patrick" },
        { content: "Hi Patrick, moi c'est BOB", type: "admin" },
      ],
      autor: "",
      message: "",
      formIsFull: false,
      justSubmitted: false,
      sending: false
    };
    this.gooDotRef = React.createRef();
    this.gooRectRef = React.createRef();
    this.submitBtnRef = React.createRef();
    this.chatRef = React.createRef();
    this.pinnedToBottom = true; // vrai tant que l'utilisateur n'a pas remonté
    this.lastGoo = { x: 0, y: 0 };
  }

  // Cale la conversation sur les derniers messages (le bas), tout en
  // laissant remonter vers l'historique.
  scrollToBottom = () => {
    const el = this.chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // Ne recale sur le bas que si l'utilisateur y était déjà (sinon on ne
  // perturbe pas sa lecture de l'historique). Utile quand une image/gif
  // finit de charger et agrandit la conversation.
  maybeStickBottom = () => {
    if (this.pinnedToBottom) this.scrollToBottom();
  };

  handleChatScroll = () => {
    const el = this.chatRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
    this.pinnedToBottom = distance < 40;
  };

  // Métaballes : la forme "curseur" suit la souris (avec la même déformation
  // que la goutte), la forme "bouton" n'apparaît qu'à l'approche — le filtre
  // goo les fait alors fusionner.
  handleGooMove = (e) => {
    if (!this.state.formIsFull) return;
    const dot = this.gooDotRef.current;
    const rect = this.gooRectRef.current;
    const btn = this.submitBtnRef.current;
    if (!dot || !rect || !btn) return;

    const x = e.clientX;
    const y = e.clientY;

    // La forme du bouton n'existe que lorsque le curseur approche
    const r = caleSurBouton(rect, btn);
    // Zone serrée autour du bouton : le bleu apparaît et le texte passe en beige
    const ZONE = 35;
    const inZone =
      x > r.left - ZONE && x < r.right + ZONE &&
      y > r.top - ZONE && y < r.bottom + ZONE;
    rect.style.opacity = inZone ? '1' : '0';
    btn.classList.toggle('goo-hot', inZone);

    // Même déformation que la goutte du site — pas de magnétisme,
    // la goutte suit librement la souris et fusionne au contact
    const dx = x - this.lastGoo.x;
    const dy = y - this.lastGoo.y;
    const speed = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    const stretch = Math.min(1 + speed / 20, 2.5);
    dot.style.visibility = 'visible';
    // +15px : même décalage que la goutte normale, pour éviter tout saut
    dot.style.transform = `translate(${x + 15}px, ${y + 15}px) rotate(${angle}deg) scale(${stretch}, 1)`;
    this.lastGoo = { x, y };
  };

  componentDidMount() {
    window.addEventListener('mousemove', this.handleGooMove);

    // Ouverture : on affiche d'emblée les derniers messages (bas de la conversation).
    // rAF pour attendre la mise en page (flex mobile) avant de caler.
    this.pinnedToBottom = true;
    requestAnimationFrame(this.scrollToBottom);

    // Conversations validées depuis l'admin : question du client + réponse
    getMessages().then((list) => {
      const publishedSpeech = list
        .filter((m) => m.status === 'published')
        .flatMap((m) => [
          { content: m.content, type: 'client', autor: m.autor },
          { content: m.reply, type: 'admin' },
        ]);
      if (publishedSpeech.length > 0) {
        this.setState(
          (prev) => ({ speech: [...prev.speech, ...publishedSpeech] }),
          this.scrollToBottom
        );
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // Nouveau(x) message(s) : on recale sur le bas (le plus récent).
    // On ne force pas le défilement sinon (ex. saisie), pour laisser
    // l'utilisateur remonter librement dans l'historique.
    if (prevState.speech.length !== this.state.speech.length) {
      this.scrollToBottom();
    }

    // Cache la goutte globale : la forme goo la remplace quand le formulaire est complet
    document.body.classList.toggle('goo-cursor-active', this.state.formIsFull);

    // Cale la forme goo sur le bouton dès l'activation
    const rect = this.gooRectRef.current;
    const btn = this.submitBtnRef.current;
    if (this.state.formIsFull && rect && btn) {
      caleSurBouton(rect, btn);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('mousemove', this.handleGooMove);
    document.body.classList.remove('goo-cursor-active');
  }

  updateField = (field, value) => {
    this.setState(prevState => {
      const next = { ...prevState, [field]: value };
      return {
        [field]: value,
        formIsFull: !!(next.autor && next.message)
      };
    });
  }

  handleSubmit = async () => {
    const { autor, message } = this.state;
    if (!(autor && message)) return;
    if (this.state.sending) return; // évite les doubles envois

    // Sans base configurée, un message resterait sur l'ordinateur du visiteur
    // et ne parviendrait jamais à l'admin : on n'affiche pas de fausse
    // confirmation (le texte saisi reste en place).
    if (!isSupabaseConfigured && process.env.NODE_ENV === 'production') return;

    this.setState({ sending: true });

    // On attend la confirmation d'enregistrement AVANT d'afficher le message
    // comme envoyé : sinon un échec (réseau, base) passerait inaperçu et le
    // message serait perdu alors que le visiteur le croirait parti.
    const saved = await addMessage({ autor, mail: '', content: message });

    if (!saved) {
      // Échec : rien n'est effacé ni affiché comme envoyé — le visiteur voit
      // que son texte est toujours là et peut réessayer.
      this.setState({ sending: false });
      return;
    }

    // Enregistré : on l'affiche en bulle "en attente" (grisée)
    const newMessage = { autor, content: message, type: 'client', pending: true };

    this.setState(prevState => ({
      speech: [...prevState.speech, newMessage],
      autor: "",
      message: "",
      formIsFull: false,
      justSubmitted: true,
      sending: false
    }));
    setTimeout(() => this.setState({ justSubmitted: false }), 550);

    // Notification mail (désactivée pour l'instant — voir EMAIL_NOTIFICATIONS_ENABLED)
    if (EMAIL_NOTIFICATIONS_ENABLED) {
      emailjs.send(
        'gmail',
        'template_J1d5IncJ',
        {
          message_html: newMessage.content,
          from_name: newMessage.autor
        },
        'user_xxxxxxxxxxxxxxxxx'
      ).then(
        (result) => {
          console.log("Email envoyé avec succès:", result.text);
        },
        (error) => {
          console.error("Erreur d'envoi:", error.text);
        }
      );
    }
  }

  render() {
    return (
      <div className="talk-content">
        {/* Zone des messages */}
        <div className="chat-content" ref={this.chatRef} onScroll={this.handleChatScroll}>
          {this.state.speech.map((msg, idx) => {
            const prev = this.state.speech[idx - 1];
            const isAdminContinuation =
              msg.type === "admin" && prev && prev.type === "admin" && !msg.img && !prev.img;
            const showSpike = !msg.img && (msg.type === "client" || !isAdminContinuation);
            const shapeClass = msg.img
              ? ''
              : `bubble-shape-${msg.type}-${showSpike ? 'spike' : 'plain'}`;

            return (
              <div key={idx} className={`message-bubble ${msg.type} ${msg.pending ? 'pending' : ''}`}>
                <div className="bubble-container">
                  {msg.autor && <span className="author-name">{msg.autor}</span>}
                  <div className={`bubble ${msg.img ? 'has-image' : shapeClass}`}>
                    <div className="bubble-content">
                      {msg.img ? (
                        <img src={msg.img} alt={msg.alt || "gif"} onLoad={this.maybeStickBottom} />
                      ) : (
                        msg.content
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="chat-bottom-spacer" />
        </div>

        {/* Couche métaballes : curseur + bouton fusionnent (filtre goo) */}
        {this.state.formIsFull && (
          <div className="goo-layer" aria-hidden="true">
            <svg width="0" height="0" style={{ position: 'absolute' }}>
              <defs>
                <filter id="goo-filter">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="9" result="blur" />
                  <feColorMatrix
                    in="blur"
                    mode="matrix"
                    values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 30 -14"
                  />
                </filter>
              </defs>
            </svg>
            <div className="goo-shapes">
              <div ref={this.gooDotRef} className="goo-dot" />
              <div ref={this.gooRectRef} className="goo-rect" />
            </div>
          </div>
        )}

        {/* Zone du formulaire */}
        <div className="message-form">
          <input
            onChange={(e) => this.updateField('autor', e.target.value)}
            className="form-input"
            type="text"
            placeholder="Nom"
            value={this.state.autor}
          />
          <textarea
            onChange={(e) => this.updateField('message', e.target.value)}
            className="form-textarea"
            placeholder="Entre ton message ici"
            value={this.state.message}
          />
          <button
            ref={this.submitBtnRef}
            onClick={this.handleSubmit}
            disabled={this.state.sending}
            className={[
              "submit-button",
              this.state.formIsFull ? "active goo-armed" : "",
              this.state.justSubmitted ? "just-submitted" : ""
            ].join(" ").trim()}
          >
            Envoyé
          </button>
        </div>
      </div>
    );
  }
}

export default Message;